/**
 * Patient class
 *
 * Used to represent a patient. Contains methods that help
 * programmer interface with Patient object.
 */

import { SimPerson } from './sim-person.model';
import { MedicalCondition } from './medical-condition.model';
import { GeneralInfo } from './general-info.model';
import { HistoryAndPhysical } from './hist-and-phys.model';
import { Timestamp } from 'firebase/firestore';
import { Vital } from './vitals.model';
import { Note } from './note.model';
import { Assessment } from './assessment.model';
import { ProviderOrder } from './provider-order.model';
import { CarePlan } from './care-plan.model';
import { PATIENT_TYPE_OBSTETRIC, PATIENT_TYPE_PEDIATRIC } from 'src/app/global';
import { OrderSet } from './order-set.model';

export type PatientType = 'Adult' | 'Pediatric' | 'Obstetric' | 'unassigned';

export class Patient {
  id: string; // ID is the unique identifier for patient
  mrn: string; // MRN is the ID of the parent patient; used to reference changes in parent patient
  firstName: string;
  lastName: string;
  middleName: string | null;
  dateCreated: Timestamp;
  currentDate: Timestamp; // used to build all other times around a set time

  // General info
  generalInfo: GeneralInfo;

  interdisciplinaryTeam: SimPerson[];
  activeMedicalProblems: MedicalCondition[];
  allergies: MedicalCondition[];

  // History and Physical Information
  historyAndPhysical: HistoryAndPhysical;

  // Orders
  nursingOrders: Note[];
  providerOrders: ProviderOrder[];

  // Collection of nursingOrders and providerOrders
  orderSets: OrderSet[];

  // Progress Notes
  progressNotes: Note[];

  // Nursing Assessment
  assessments: Assessment[];

  //Care Plan
  carePlan: CarePlan[];

  // Vitals
  vitals: Vital[];

  // Profile picture name
  profilePicName?: string;

  // Uploaded document name
  uploadedDocuments?: File[] = [];

  constructor(id?: string, mrn?: string, firstName?: string, lastName?: string) {
    this.id = id ? id : '';
    this.mrn = mrn ? mrn : '';
    this.firstName = firstName ? firstName : '';
    this.lastName = lastName ? lastName : '';
    this.middleName = '';
    this.dateCreated = Timestamp.now();
    this.currentDate = Timestamp.now();
    this.generalInfo = new GeneralInfo();
    this.historyAndPhysical = new HistoryAndPhysical();
    this.interdisciplinaryTeam = [];
    this.activeMedicalProblems = [];
    this.allergies = [];
    this.nursingOrders = [];
    this.providerOrders = [];
    this.orderSets = [];
    this.progressNotes = [];
    this.assessments = [];
    this.vitals = [];
    this.carePlan = [];
    this.uploadedDocuments = [];
  }

  getId() {
    return this.id;
  }

  getMRN() {
    return this.mrn;
  }

  getLastFirstMiddleName() {
    return this.middleName
      ? `${this.lastName}, ${this.firstName} ${this.middleName[0]}.`
      : `${this.lastName}, ${this.firstName}`;
  }

  getNameFirstMiddleLast() {
    return this.middleName
      ? `${this.firstName} ${this.middleName} ${this.lastName}`
      : `${this.firstName} ${this.lastName}`;
  }

  getDisplayDateCreated(): string {
    const date = this.dateCreated.toDate();
    return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
  }

  getCurrentDateAsDate(): Date {
    return new Date(this.currentDate.seconds * 1000);
    // return this.currentDate.toDate();
  }

  getCurrentDateAsTimestamp(): Timestamp {
    return this.currentDate;
  }

  getCodeStatus(): string {
    return this.generalInfo.codeStatus;
  }

  getDOB(): Timestamp {
    return this.generalInfo.dob;
  }

  getDOBString(): string {
    return this.generalInfo.dob.toDate().toLocaleDateString();
  }

  /**
   * This calculates age based on UTC, so the age may be off by one depending on
   * the location of the machine the app is being run on.
   *
   * @returns The user's age calculated from their date of birth.
   */
  // getAge(): number {
  //   const currentDate = new Date();
  //   const birthDate = this.generalInfo.dob.toDate();

  //   // Subtract current year from birth year to get the age
  //   let age = currentDate.getUTCFullYear() - birthDate.getUTCFullYear();

  //   const monthDiff = currentDate.getUTCMonth() - birthDate.getUTCMonth();

  //   // Subtract one year if birth month is ahead of current month
  //   if(monthDiff < 0) {
  //     age -= 1;
  //   }
  //   // Or subtract one year if the birth months are the same, but the birth day is ahead
  //   //  of the current day
  //   else if(monthDiff == 0) {
  //     const dayDiff = currentDate.getUTCDate() - birthDate.getUTCDate();
  //     if(dayDiff < 0) {
  //       age -= 1;
  //     }
  //   }

  //   return age;
  // }

  getAge(): number {
    return this.generalInfo.age;
  }

  getSex() {
    return this.generalInfo.sex;
  }

  getWeight(): number {
    return this.generalInfo.weight;
  }

  getBMI(): number {
    try {
      const heightMeters = 0.0254 * this.generalInfo.heightInches;
      return Math.round((this.generalInfo.weight / Math.pow(heightMeters, 2)) * 100) / 100; // to get 2 decimal precision
    } catch (err) {
      console.error(err);
      return -1;
    }
  }

  getIsolation(): string {
    return this.generalInfo.isolation;
  }

  getHeightInches(): number {
    return this.generalInfo.heightInches;
  }

  getHeightString(): string {
    const feet = Math.floor(this.generalInfo.heightInches / 12);
    const inches = this.generalInfo.heightInches % 12;
    return `${feet}'${inches}"`;
  }

  getRoomNumber(): string {
    return this.generalInfo.roomNumber;
  }
  getLocation(): string {
    return this.generalInfo.location;
  }
  getType(): PatientType {
    return this.generalInfo.patientType;
  }

  isObstetric(): boolean {
    return this.generalInfo.patientType === PATIENT_TYPE_OBSTETRIC;
  }

  isPediatric(): boolean {
    return this.generalInfo.patientType === PATIENT_TYPE_PEDIATRIC;
  }


  // start getters for history and physical information
  getAllergies(): string {
    return this.historyAndPhysical.allergies;
  }
  getCardiovascular(): string {
    return this.historyAndPhysical.cardiovascular;
  }
  getChiefComplaint(): string {
    return this.historyAndPhysical.chiefComplaint;
  }
  getChiefInformant(): string {
    return this.historyAndPhysical.chiefInformant;
  }
  getContagiousDiseases(): string {
    return this.historyAndPhysical.contagiousDiseases;
  }
  getCurrentMeds(): string {
    return this.historyAndPhysical.currentMeds;
  }
  getDevelopmental(): string {
    return this.historyAndPhysical.developmental;
  }
  getDietHistory(): string {
    return this.historyAndPhysical.dietaryHistory;
  }
  getEndocrine(): string {
    return this.historyAndPhysical.endocrine;
  }
  getFamilyHistory(): string {
    return this.historyAndPhysical.familyHistory;
  }
  getGastrointestinal(): string {
    return this.historyAndPhysical.gastrointestinal;
  }
  getGenitoUrinary(): string {
    return this.historyAndPhysical.genitourinary;
  }
  getHEENT(): string {
    return this.historyAndPhysical.heent;
  }
  getHistOfCurrProb(): string {
    return this.historyAndPhysical.histOfCurrProb;
  }
  getImpressions(): string {
    return this.historyAndPhysical.impressions;
  }
  getImpressionsDay(): string {
    return this.historyAndPhysical.impressionsDay;
  }
  getInjuries(): string {
    return this.historyAndPhysical.injuries;
  }
  getIntegument(): string {
    return this.historyAndPhysical.integument;
  }
  getLymphatic(): string {
    return this.historyAndPhysical.lymphatic;
  }
  getMusculoskeletal(): string {
    return this.historyAndPhysical.musculoskeletal;
  }
  getNeurologic(): string {
    return this.historyAndPhysical.neurologic;
  }
  getOtherPastMedHist(): string {
    return this.historyAndPhysical.otherPastMedHist;
  }
  getPlan(): string {
    return this.historyAndPhysical.plan;
  }
  getPreviousIllness(): string {
    return this.historyAndPhysical.previousIllness;
  }
  getProvSignature(): string {
    return this.historyAndPhysical.provSignature;
  }
  getRespiratory(): string {
    return this.historyAndPhysical.respiratory;
  }
  getSocialHistory(): string {
    return this.historyAndPhysical.socialHistory;
  }
  getSurgicalHistory(): string {
    return this.historyAndPhysical.surgicalHistory;
  }

  // getters for physical examination portion of hist and phys
  getPECardio(): string {
    return this.historyAndPhysical.peCardio;
  }
  getPEDevelopmental(): string {
    return this.historyAndPhysical.peDevelopmental;
  }
  getPEEndocrine(): string {
    return this.historyAndPhysical.peEndocrine;
  }
  getPEGastrointestinal(): string {
    return this.historyAndPhysical.peGastrointestinal;
  }
  getPEGeneral(): string {
    return this.historyAndPhysical.peGeneral;
  }
  getPEGenitourinary(): string {
    return this.historyAndPhysical.peGenitourinary;
  }
  getPEHEENT(): string {
    return this.historyAndPhysical.peHEENT;
  }
  getPELymphatic(): string {
    return this.historyAndPhysical.peLymphatic;
  }
  getPEMusculoskeletal(): string {
    return this.historyAndPhysical.peMusculoskeletal;
  }
  getPENeurologic(): string {
    return this.historyAndPhysical.peNeurologic;
  }
  getPERespiratory(): string {
    return this.historyAndPhysical.peRespiratory;
  }
  getPESkin(): string {
    return this.historyAndPhysical.peSkin;
  }
  getPEVitalSigns(): string {
    return this.historyAndPhysical.peVitalSigns;
  }
  getPEProviderNotified(): string {
    return this.historyAndPhysical.peProviderNotified;
  }
  // end getters for hist and phy info

  getProfilePicURL(): string {
    return this.profilePicName && this.profilePicName !== 'blank-profile.webp'
      ? `${this.id}/${this.profilePicName}`
      : 'blank-profile.webp';
  }



  setId(id: string): void {
    this.id = id;
  }

  setMRN(mrn: string): void {
    this.mrn = mrn;
  }

  setDOB(date: Timestamp): void {
    this.generalInfo.dob = date;
  }

  setHeight(heightInches: number): void {
    this.generalInfo.heightInches = heightInches;
  }

  setRoomNumber(roomNumber: string): void {
    this.generalInfo.roomNumber = roomNumber;
  }
  setLocation(location: string): void {
    this.generalInfo.location = location;
  }
  setType(type: PatientType): void {
    this.generalInfo.patientType = type;
  }

  /**
   * setProfilePicName
   *
   * @param imgName name of the image used for the user's profile picture
   *  in Firebase, image paths are of the form <id>/profile.<jpg|png|gif>
   *
   */
  setProfilePicName(imgName: string) {
    this.profilePicName = imgName;
  }

  public toJSObject(): void {
    this.nursingOrders = this.nursingOrders.map((order) => {
      return new Note(
        order.id,
        order.simDateCreated,
        order.title,
        order.content,
        order.signature,
        order.dateCreated,
        order.isInOrderSet,
      );
    });

    this.providerOrders = this.providerOrders.map((order: ProviderOrder) => {
      return new ProviderOrder(
        order.id,
        order.medId,
        order.medName,
        order.medControlledSubstance,
        order.route,
        order.dosage,
        order.quantity,
        order.quantityGiven,
        order.whichGiven,
        order.type,
        order.orderNotes,
        order.signature,
        order.startTime,
        order.prnLastGivenTime,
        order.prnDosingInstructions,
        order.slidingScaleTable,
        order.mealTimesTable,
        order.isInOrderSet,
        order.isScheduled,
      );
    });

    this.progressNotes = this.progressNotes.map((note) => {
      return new Note(
        note.id,
        note.simDateCreated,
        note.title,
        note.content,
        note.signature,
        note.dateCreated
      );
    });

    this.vitals = this.vitals.map((vital) => {
      return new Vital(
        vital.date,
        vital.heartRate,
        vital.oxySat,
        vital.oxygen,
        vital.oxygenInput,
        vital.respirations,
        vital.temp,
        vital.systolic,
        vital.diastolic,
        vital.deleted
      );
    });

    this.carePlan = this.carePlan.map((notes) => {
      return new CarePlan(
        notes.noticing,
        notes.interpreting,
        notes.responding,
        notes.reflecting,
        notes.lastUpdated
      );
    });
  }

  /**
   * Prepares the Patient's format for Firebase by converting the patient to JSON,
   * with the object's dates being Timestamps.
   *
   * @returns a Patient represented as JSON.
   */
  public toFirebase() {
    const obj = JSON.parse(JSON.stringify(Object.assign({}, this)));

    // Delete the ID field from object
    delete obj.id;

    // Convert {seconds, nanoseconds} object produced by JSON.parse to Timestamp object
    obj.dateCreated = Timestamp.fromMillis(obj.dateCreated.seconds * 1000);
    obj.currentDate = Timestamp.fromMillis(obj.currentDate.seconds * 1000);
    obj.generalInfo.dob = Timestamp.fromMillis(obj.generalInfo.dob.seconds * 1000);

    // Call toFirebase() in contained classes
    this.datesToFirebase(obj.assessments, ['date']);
    this.datesToFirebase(obj.carePlan, ['date']);
    this.datesToFirebase(obj.nursingOrders, ['simDateCreated', 'dateCreated']);
    this.datesToFirebase(obj.progressNotes, ['simDateCreated', 'dateCreated']);
    this.datesToFirebase(obj.vitals, ['date']);
    this.datesToFirebase(obj.uploadedDocuments, ['date']);

    return obj;
  }

  // add assessmentsToFirebase to handle gastrointestinal dates

  /**
   *
   * @param objArray An array of objects to iterate over and convert the specified fields to Timestamps
   * @param dateFields The fields to convert to Timestamps
   */
  private datesToFirebase(objArray: any[], dateFields: string[]) {
    objArray = objArray.map((obj) => {
      dateFields.forEach((field) => {
        if (field in obj && obj[field] !== null) {
          obj[field] = Timestamp.fromMillis(obj[field].seconds * 1000);
        }
      });
    });
  }
}
